import { z } from "zod";

/**
 * Plans, prices and what each one unlocks — the catalogue.
 *
 * Everything commercial lives in this one file so that changing a price or a limit is
 * an edit here and nowhere else. The API charges from it, the join path caps audiences
 * from it, and the web app renders its plan cards from it, so the three cannot drift.
 */

export const PLAN_IDS = ["free", "starter", "pro", "business", "enterprise"] as const;
export type PlanId = (typeof PLAN_IDS)[number];
export const planIdSchema = z.enum(PLAN_IDS);

/** The plans an organisation can buy for itself. Enterprise is arranged by hand. */
export const PAID_PLANS = ["starter", "pro", "business"] as const;
export type PaidPlanId = (typeof PAID_PLANS)[number];
export const paidPlanSchema = z.enum(PAID_PLANS);

/**
 * What a plan unlocks beyond audience size.
 *
 * - `themes`: the audience sees the deck's own theme and logo, not the default look.
 * - `lobby`: the lobby's layout, title and welcome message.
 * - `exports`: results and Q&A as spreadsheets, and the printable report.
 */
export const FEATURES = ["themes", "lobby", "exports"] as const;
export type Feature = (typeof FEATURES)[number];

export interface PlanDefinition {
  /** Participants admitted to one session. Enterprise's comes from its subscription row. */
  participantLimit: number;
  /**
   * Presenter-led sessions the organisation may have open at once (lobby or live). What
   * stops one paid login being shared round a room of presenters: however many people
   * hold it, they run this many rooms at a time. Self-paced sessions do not count.
   */
  liveSessionLimit: number;
  /** People in the organisation, counting invitations still waiting to be accepted. */
  memberLimit: number;
  features: readonly Feature[];
}

export const PLANS: Record<PlanId, PlanDefinition> = {
  free: { participantLimit: 50, liveSessionLimit: 1, memberLimit: 1, features: [] },
  starter: { participantLimit: 150, liveSessionLimit: 1, memberLimit: 1, features: ["exports"] },
  pro: { participantLimit: 300, liveSessionLimit: 2, memberLimit: 3, features: ["themes", "lobby", "exports"] },
  business: { participantLimit: 2000, liveSessionLimit: 5, memberLimit: 10, features: ["themes", "lobby", "exports"] },
  enterprise: { participantLimit: 10_000, liveSessionLimit: 50, memberLimit: 100, features: ["themes", "lobby", "exports"] },
};

/**
 * How long an open presenter-led session counts against `liveSessionLimit`. A presenter
 * who closes the tab without ending the session would otherwise hold their only room
 * for good; a day's event fits well inside it.
 */
export const LIVE_SESSION_HOURS = 12;

/**
 * How long a payment buys. `week` is the event pass: seven days of Business, paid once
 * and never renewed (PASS_PLAN), for an organisation with one big event rather than a
 * habit.
 */
export const BILLING_INTERVALS = ["week", "month", "year"] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];
export const billingIntervalSchema = z.enum(BILLING_INTERVALS);

/**
 * Periods are counted in days, not calendar months. Someone paying by M-Pesa buys
 * "30 days", which is what they will be told, and it makes the value of unused time
 * the same arithmetic whichever interval it was bought on.
 */
export const INTERVAL_DAYS: Record<BillingInterval, number> = { week: 7, month: 30, year: 365 };

/** The intervals a plan renews on; the pass is not one. */
export const SUBSCRIPTION_INTERVALS = ["month", "year"] as const satisfies readonly BillingInterval[];

/** The event pass: this plan, for a week. */
export const PASS_PLAN = "business" as const satisfies PaidPlanId;
export const PASS_INTERVAL = "week" as const satisfies BillingInterval;

/**
 * How long a plan keeps working after its period ends. Covers a card renewal that
 * failed once and a prepaid customer who pays a day late, without either of them
 * losing their branding halfway through an event.
 */
export const GRACE_DAYS = 3;

/**
 * How long before a period ends its managers are emailed to pay again, when nothing will
 * renew it by itself — M-Pesa, or a card with renewal turned off. Long enough to find a
 * phone and a PIN before an event, short enough not to read as a sales email.
 */
export const REMIND_DAYS = 3;

export const CURRENCIES = ["KES", "USD"] as const;
export type Currency = (typeof CURRENCIES)[number];
export const currencySchema = z.enum(CURRENCIES);

/**
 * What each plan is sold for, by the week (the event pass), month or year. Fixed here
 * rather than with the prices: an interval decides how a plan behaves — a pass never
 * renews — so adding one is a change to the product, not to a number.
 */
export const OFFERED_INTERVALS: Record<PaidPlanId, readonly BillingInterval[]> = {
  starter: ["month", "year"],
  pro: ["month", "year"],
  business: ["week", "month", "year"],
};

/**
 * Prices in minor units (cents), per plan, currency and interval. Whole units only:
 * M-Pesa charges whole shillings, and a price it would have to round is not one to quote.
 *
 * The live prices are a table (`plan_prices`) a platform admin edits (ADR 0009), read
 * through `PriceBook` in packages/payments. Every price is a `PriceList` handed in, so
 * nothing can quote one figure and charge another.
 */
export type PriceList = Record<PaidPlanId, Record<Currency, Partial<Record<BillingInterval, number>>>>;

/**
 * The prices a new deployment starts with, which the migration that made `plan_prices`
 * wrote into it; and what any price missing from that table falls back to.
 */
export const DEFAULT_PRICES: PriceList = {
  starter: {
    KES: { month: 3_000_00, year: 30_000_00 },
    USD: { month: 23_00, year: 230_00 },
  },
  pro: {
    KES: { month: 7_000_00, year: 70_000_00 },
    USD: { month: 54_00, year: 540_00 },
  },
  business: {
    KES: { week: 4_000_00, month: 15_000_00, year: 150_000_00 },
    USD: { week: 30_00, month: 116_00, year: 1_160_00 },
  },
};

/** The most one price may be: well past any plan, short of a typo that adds three zeros. */
export const MAX_PRICE_MINOR = 100_000_000_00;

const priceSchema = z.number().int().positive().max(MAX_PRICE_MINOR).multipleOf(100);

/** A whole price list: every offered interval of every plan, in every currency, and nothing else. */
export const priceListSchema = z
  .object(
    Object.fromEntries(
      PAID_PLANS.map((plan) => [
        plan,
        z
          .object(
            Object.fromEntries(
              CURRENCIES.map((currency) => [
                currency,
                z.object(Object.fromEntries(OFFERED_INTERVALS[plan].map((i) => [i, priceSchema]))).strict(),
              ]),
            ),
          )
          .strict(),
      ]),
    ),
  )
  .strict() as unknown as z.ZodType<PriceList>;

/** Is this plan sold for this long? Every currency offers the same intervals. */
export function isOffered(plan: PaidPlanId, interval: BillingInterval): boolean {
  return OFFERED_INTERVALS[plan].includes(interval);
}

export function priceOf(prices: PriceList, plan: PaidPlanId, currency: Currency, interval: BillingInterval): number {
  const price = isOffered(plan, interval) ? prices[plan][currency][interval] : undefined;
  if (price === undefined) throw new Error(`${plan} is not sold by the ${interval}`);
  return price;
}

/** Every price in a list, one row each, in the catalogue's order. */
export function priceRows(prices: PriceList): { plan: PaidPlanId; currency: Currency; interval: BillingInterval; amountMinor: number }[] {
  return PAID_PLANS.flatMap((plan) =>
    OFFERED_INTERVALS[plan].flatMap((interval) =>
      CURRENCIES.map((currency) => ({ plan, currency, interval, amountMinor: priceOf(prices, plan, currency, interval) })),
    ),
  );
}

export function isPaidPlan(plan: PlanId): plan is PaidPlanId {
  return (PAID_PLANS as readonly string[]).includes(plan);
}

// ---------------------------------------------------------------------------
// Entitlements
// ---------------------------------------------------------------------------

/** The columns of an organisation's subscription that decide what it may do. */
export interface SubscriptionTerms {
  plan: PlanId;
  /** Null for a plan that does not expire — free, or enterprise arranged by hand. */
  currentPeriodEnd: Date | null;
  /** Enterprise only; every other plan takes its limit from the catalogue. */
  participantLimit?: number | null;
}

export const entitlementsSchema = z.object({
  plan: planIdSchema,
  participantLimit: z.number().int().positive(),
  liveSessionLimit: z.number().int().positive(),
  memberLimit: z.number().int().positive(),
  features: z.array(z.enum(FEATURES)),
});
export type Entitlements = z.infer<typeof entitlementsSchema>;

/**
 * What an organisation may do right now.
 *
 * Derived on every read from the period end rather than stored, so nothing has to run
 * at midnight to downgrade anyone: a lapsed plan is free the moment its grace ends,
 * whether or not the worker was up. No subscription row at all means free.
 */
export function entitlementsFor(terms: SubscriptionTerms | null, now: Date): Entitlements {
  const plan = effectivePlan(terms, now);
  const def = PLANS[plan];
  const participantLimit =
    plan === "enterprise" && terms?.participantLimit ? terms.participantLimit : def.participantLimit;
  return {
    plan,
    participantLimit,
    liveSessionLimit: def.liveSessionLimit,
    memberLimit: def.memberLimit,
    features: [...def.features],
  };
}

export function hasFeature(entitlements: Pick<Entitlements, "features">, feature: Feature): boolean {
  return entitlements.features.includes(feature);
}

function effectivePlan(terms: SubscriptionTerms | null, now: Date): PlanId {
  if (!terms || terms.plan === "free") return "free";
  if (terms.currentPeriodEnd === null) return terms.plan;
  return now.getTime() < graceEnd(terms.currentPeriodEnd).getTime() ? terms.plan : "free";
}

export function graceEnd(periodEnd: Date): Date {
  return new Date(periodEnd.getTime() + GRACE_DAYS * DAY_MS);
}

/**
 * Where a subscription stands, for the billing page.
 *
 * - `free`: nothing bought, or bought long ago and lapsed.
 * - `active`: inside a paid period.
 * - `grace`: the period has ended and the plan still works for GRACE_DAYS.
 */
/**
 * Whether the organisation has a plan in force: Free, chosen once and kept for good, or
 * a paid plan still inside its period or grace days. Without one — an organisation that
 * has never chosen, or whose paid plan has lapsed — it must choose (Free or paid) before
 * it can make or change anything. Reading what it already has, and paying, stay open.
 */
export function hasPlan(terms: SubscriptionTerms | null, now: Date): boolean {
  if (!terms) return false;
  if (terms.plan === "free" || terms.currentPeriodEnd === null) return true;
  return now.getTime() < graceEnd(terms.currentPeriodEnd).getTime();
}

export type SubscriptionState = "free" | "active" | "grace";

export function subscriptionState(terms: SubscriptionTerms | null, now: Date): SubscriptionState {
  if (!terms || effectivePlan(terms, now) === "free") return "free";
  if (terms.currentPeriodEnd === null || now < terms.currentPeriodEnd) return "active";
  return "grace";
}

// ---------------------------------------------------------------------------
// Periods
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;

export interface CurrentPeriod {
  plan: PlanId;
  interval: BillingInterval | null;
  currentPeriodEnd: Date | null;
}

/**
 * When a period just paid for ends.
 *
 * One rule for every purchase: a fresh period from now, plus the unused time left on
 * the current plan, converted at the two plans' daily prices. So renewing or topping
 * up the same plan simply extends it; upgrading turns leftover Pro days into fewer
 * Business days; downgrading turns them into more. Nobody pays twice for the same day
 * and nothing needs a refund.
 *
 * Both daily prices are taken in the currency being paid, so the ratio is meaningful
 * even if the earlier period was bought in another one.
 */
export function periodEndAfterPurchase(args: {
  now: Date;
  current: CurrentPeriod | null;
  plan: PaidPlanId;
  interval: BillingInterval;
  currency: Currency;
  prices: PriceList;
}): Date {
  const { now, current, plan, interval, currency, prices } = args;
  const fresh = now.getTime() + INTERVAL_DAYS[interval] * DAY_MS;

  if (
    !current ||
    !isPaidPlan(current.plan) ||
    !current.interval ||
    !current.currentPeriodEnd ||
    current.currentPeriodEnd <= now
  ) {
    return new Date(fresh);
  }

  const remainingMs = current.currentPeriodEnd.getTime() - now.getTime();
  // The plan being left is valued at today's price for it, which is what its days would
  // cost to buy now; a price change since it was bought neither costs nor gains them.
  const oldDaily = priceOf(prices, current.plan, currency, current.interval) / INTERVAL_DAYS[current.interval];
  const newDaily = priceOf(prices, plan, currency, interval) / INTERVAL_DAYS[interval];
  // Whole seconds: the credit is a convenience, not a ledger, and sub-second periods
  // only make timestamps harder to read.
  const creditMs = Math.floor((remainingMs * oldDaily) / newDaily / 1000) * 1000;
  return new Date(fresh + creditMs);
}

// ---------------------------------------------------------------------------
// API contracts
// ---------------------------------------------------------------------------

/**
 * The payment vendors. One: Paystack takes cards and M-Pesa alike on its own checkout
 * page. Kept as a list because the adapter seam (Rule 5) is what a second would plug into.
 */
export const PAYMENT_PROVIDERS = ["paystack"] as const;
export type PaymentProviderId = (typeof PAYMENT_PROVIDERS)[number];

export const checkoutRequestSchema = z
  .object({
    plan: paidPlanSchema,
    interval: billingIntervalSchema,
    /** One the deployment offers (`BillingOverview.currencies`); its default when left out. */
    currency: currencySchema.optional(),
  })
  .strict()
  .refine((r) => isOffered(r.plan, r.interval), { message: "That plan isn't sold for that period", path: ["interval"] });
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

/**
 * Where to send the browser: Paystack's checkout, where the customer chooses a card or
 * M-Pesa, and from which they come back to the billing page.
 */
export const checkoutResultSchema = z.object({ paymentId: z.string().uuid(), url: z.string().url() });
export type CheckoutResult = z.infer<typeof checkoutResultSchema>;

export const PAYMENT_STATUSES = ["pending", "succeeded", "failed", "refunded"] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const paymentSchema = z.object({
  id: z.string().uuid(),
  provider: z.enum(["mpesa", "paystack", "stripe", "manual"]),
  kind: z.enum(["checkout", "renewal"]),
  plan: planIdSchema.nullable(),
  interval: billingIntervalSchema.nullable(),
  amountMinor: z.number().int(),
  currency: z.string(),
  status: z.enum(PAYMENT_STATUSES),
  /** How it was paid, safe to show: "Visa •••• 4081", "M-Pesa •••• 5678". */
  payer: z.string().nullable(),
  /** The vendor's receipt: an M-Pesa confirmation code, or the Paystack reference. */
  receipt: z.string().nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.string(),
});
export type Payment = z.infer<typeof paymentSchema>;

export const billingOverviewSchema = z.object({
  /** The plan paid for, which may have lapsed — `entitlements.plan` is what applies. */
  plan: planIdSchema,
  entitlements: entitlementsSchema,
  state: z.enum(["free", "active", "grace"]),
  interval: billingIntervalSchema.nullable(),
  currentPeriodEnd: z.string().nullable(),
  graceEndsAt: z.string().nullable(),
  /** A saved card will be charged when the period ends. */
  autoRenew: z.boolean(),
  /** The last automatic charge was declined; it will be retried. */
  renewalFailed: z.boolean(),
  /** The saved card, safe to show: "Visa •••• 4081". */
  paymentMethod: z.string().nullable(),
  /** Owners and admins pay; members see the plan but cannot change it. */
  canManage: z.boolean(),
  /** False when this deployment takes no payments: nothing is offered for sale. */
  forSale: z.boolean(),
  /**
   * No plan in force (`hasPlan`): never chosen, or a paid plan that lapsed. Until an
   * owner or admin chooses one, decks cannot be made or changed nor sessions started.
   * Always false on a deployment that does not bill.
   */
  needsPlan: z.boolean(),
  /**
   * What plans are shown in first: the currency the plan was last paid in, or the
   * deployment's default.
   */
  currency: currencySchema,
  /** Every currency a customer may pay in, the deployment's default first. */
  currencies: z.array(currencySchema),
  /** What each plan costs now: the plan cards show exactly what checkout will charge. */
  prices: priceListSchema,
});
export type BillingOverview = z.infer<typeof billingOverviewSchema>;

export const autoRenewRequestSchema = z.object({ enabled: z.boolean() }).strict();
export type AutoRenewRequest = z.infer<typeof autoRenewRequestSchema>;
